import * as React from 'react';
import { useTheme } from '../../../../../theme';
import { Form, FormSpy } from 'react-final-form';
import Field from './Field/Field';
import { useTranslation } from 'react-i18next';
import { FfpAccountFieldEnum, useCitySuggestionsQuery } from '@websky/graphql';
import { InputType } from '../../../../../Passenger/types';
import cn from 'classnames';
import ModalConfirm from '../../../../../ModalConfirm';
import { ItemIncluded } from '../../../../../Icons';
import { Tooltip } from '../../../../../BaseComponents';
import { API_DATE_FORMAT, convertGenderToCorrectValue, format } from '../../../../../utils';
import { parse } from 'date-fns';
import { useDebounce } from '../../../../../hooks';
import { CdnIcon } from '@shared/ui';
import { parsePhoneNumber } from 'libphonenumber-js';
import { SafeHtml } from '@shared/ui';
const blocks = [
    {
        title: 'Your name, Cyrillic',
        groups: [[FfpAccountFieldEnum.LastName], [FfpAccountFieldEnum.FirstName], [FfpAccountFieldEnum.MiddleName]]
    },
    {
        title: 'Your name, Latin',
        subtitle: 'Please enter Full name in Latin transcription',
        groups: [
            [FfpAccountFieldEnum.LatinLastName],
            [FfpAccountFieldEnum.LatinFirstName],
            [FfpAccountFieldEnum.LatinMiddleName]
        ]
    },
    {
        title: 'Document',
        groups: [
            [FfpAccountFieldEnum.Country],
            [FfpAccountFieldEnum.DocType],
            [FfpAccountFieldEnum.DocNumber],
            [FfpAccountFieldEnum.DocExpiryDate],
            [FfpAccountFieldEnum.DocIssued],
            [FfpAccountFieldEnum.DocIssuedBy]
        ]
    },
    {
        title: 'Contacts',
        subtitle: 'How to Connect with You?',
        groups: [[FfpAccountFieldEnum.Email], [FfpAccountFieldEnum.Phone], [FfpAccountFieldEnum.CityID]]
    }
];
const CreateAccount = props => {
    var _a, _b, _c, _d;
    const theme = useTheme('Loyalty').LoyaltyConnect;
    const [citySearchValue, setCitySearchValue] = React.useState('');
    const debouncedCitySearchValue = useDebounce(citySearchValue, 300);
    const { t } = useTranslation('Loyalty');
    const getFieldType = (type) => {
        switch (type) {
            case FfpAccountFieldEnum.BirthDate:
            case FfpAccountFieldEnum.DocExpiryDate:
            case FfpAccountFieldEnum.DocIssued:
                return InputType.Date;
            case FfpAccountFieldEnum.Phone:
                return InputType.Phone;
            case FfpAccountFieldEnum.Country:
            case FfpAccountFieldEnum.DocType:
                return InputType.Select;
            case FfpAccountFieldEnum.Gender:
                return InputType.Switch;
            case FfpAccountFieldEnum.CityID:
                return InputType.Autocomplete;
            default:
                return InputType.Text;
        }
    };
    const getOptions = (validationRules, values, type) => {
        let options;
        for (const rule of validationRules) {
            if (!rule.with || rule.with.every(condition => values[condition.name] === condition.value)) {
                options = rule.options;
                break;
            }
        }
        return options.map(option => {
            return {
                key: option.value,
                value: option.label
            };
        });
    };
    const fieldList = (_b = (_a = props.initialValues) === null || _a === void 0 ? void 0 : _a.FfpDefaultAccount) === null || _b === void 0 ? void 0 : _b.values;
    const { data: citySuggestions } = useCitySuggestionsQuery({
        variables: {
            query: debouncedCitySearchValue
        },
        skip: !(fieldList === null || fieldList === void 0 ? void 0 : fieldList.some(field => field.type === FfpAccountFieldEnum.Phone)) || !debouncedCitySearchValue
    });
    const BirthdayGenderFieldGroup = [FfpAccountFieldEnum.BirthDate, FfpAccountFieldEnum.Gender]
        .map(fieldType => {
        return fieldList === null || fieldList === void 0 ? void 0 : fieldList.find(param => (param === null || param === void 0 ? void 0 : param.type) === fieldType);
    })
        .filter(p => p !== undefined);
    const formData = React.useMemo(() => {
        if (!fieldList) {
            return null;
        }
        const fieldListMap = new Map(fieldList.map(field => [field.type, field]));
        const data = blocks
            .map(block => {
            const groups = block.groups
                .map(group => group.map(fieldType => fieldListMap.get(fieldType)).filter(Boolean))
                .filter(group => group.length);
            return Object.assign(Object.assign({}, block), { groups });
        })
            .filter(block => block.groups.length);
        if (BirthdayGenderFieldGroup.length && data[0]) {
            data[0].groups.push(BirthdayGenderFieldGroup);
        }
        return data;
    }, [fieldList]);
    const [formValidationErrors, setValidationErrors] = React.useState({});
    const formBlocksValidationData = React.useMemo(() => {
        if (!formData) {
            return null;
        }
        const list = new Map(Object.entries(formValidationErrors));
        const blocksList = formData.map(block => {
            const isValidBLock = block.groups.find(gr => {
                const groupHasErrors = gr.find(val => {
                    return list.get(val.name);
                });
                return !!groupHasErrors;
            });
            return !!!isValidBLock;
        });
        return blocksList;
    }, [fieldList, formValidationErrors]);
    const onSubmit = (data) => {
        var _a, _b, _c, _d, _e;
        (_a = props.onSubmitForm) === null || _a === void 0 ? void 0 : _a.call(props);
        const params = [];
        const valuesMap = new Map((_e = (_d = (_c = (_b = props.initialValues) === null || _b === void 0 ? void 0 : _b.FfpDefaultAccount) === null || _c === void 0 ? void 0 : _c.values) === null || _d === void 0 ? void 0 : _d.map(value => [value.name, value])) !== null && _e !== void 0 ? _e : []);
        Object.entries(data).forEach(([name, value]) => {
            var _a, _b;
            let actualValue = value !== null && value !== void 0 ? value : '';
            const { type } = (_a = valuesMap.get(name)) !== null && _a !== void 0 ? _a : {};
            try {
                if (type === FfpAccountFieldEnum.CityID) {
                    actualValue = value === null || value === void 0 ? void 0 : value.value;
                }
                else if (type === FfpAccountFieldEnum.PhoneCountry) {
                    actualValue = (_b = parsePhoneNumber(data['phone'])) === null || _b === void 0 ? void 0 : _b.country;
                }
            }
            catch (e) {
                console.error(e);
            }
            params.push({
                type,
                name,
                value: (actualValue !== null && actualValue !== void 0 ? actualValue : '').trim()
            });
        });
        return props.onCreateAccount(params);
    };
    const initialValues = {};
    (_d = (_c = props.initialValues) === null || _c === void 0 ? void 0 : _c.FfpDefaultAccount) === null || _d === void 0 ? void 0 : _d.values.forEach(value => {
        if (value.type === FfpAccountFieldEnum.Gender && value.value) {
            initialValues[value.name] = convertGenderToCorrectValue(value.value);
        }
        else {
            initialValues[value.name] = value.value;
        }
        if (getFieldType(value.type) === 'date' && !!value.value) {
            try {
                initialValues[value.name] = format(parse(value.value, API_DATE_FORMAT, new Date()), 'dd.MM.yyyy');
            }
            catch (e) {
                initialValues[value.name] = format(parse(value.value, 'dd.MM.yyyy', new Date()), 'dd.MM.yyyy');
            }
        }
    });
    const getFieldOptions = (fieldType, values, validationRules, type) => {
        var _a;
        if (fieldType === InputType.Select) {
            return getOptions(validationRules, values, type);
        }
        if (fieldType === InputType.Autocomplete && type === FfpAccountFieldEnum.CityID) {
            return (((_a = citySuggestions === null || citySuggestions === void 0 ? void 0 : citySuggestions.CitySuggestions) === null || _a === void 0 ? void 0 : _a.cities.map(city => ({
                key: `${city.city.name} (${city.city.region}, ${city.country.name})`,
                value: city.city.id
            }))) || []);
        }
        return undefined;
    };
    const onCityAutocompleteChange = (value, reason) => {
        // return if option was selected
        if (value && reason === 'reset') {
            return;
        }
        setCitySearchValue(value || '');
    };
    React.useEffect(() => {
        if (!props.isOpen && props.formRef.current) {
            setTimeout(() => {
                // без setTimeout ошибка "Cannot reset() in onSubmit(), use setTimeout(form.reset)"
                props.formRef.current.reset();
            }, 0);
        }
    }, [props.isOpen]);
    if (!formData) {
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement("div", { className: theme.loyaltyConnect },
        React.createElement(Form, { onSubmit: onSubmit, initialValues: initialValues, validateOnBlur: true }, ({ handleSubmit, values, form }) => {
            props.formRef.current = form;
            return (React.createElement(React.Fragment, null,
                React.createElement("form", { onSubmit: handleSubmit },
                    React.createElement("div", { className: theme.form__content }, formData.map((block, index) => {
                        var _a;
                        return (React.createElement("div", { className: theme.block, key: index },
                            React.createElement("div", { className: theme.block__headerWrapper },
                                React.createElement("div", { className: theme.block__header },
                                    React.createElement("div", { className: theme.block__header__titleWrapper },
                                        React.createElement("div", { className: theme.block__header__titleNumber }, index + 1),
                                        React.createElement("div", { className: theme.block__header__title }, t(block.title))),
                                    formBlocksValidationData[index] && (React.createElement("div", { className: theme.block__header__valid }, ItemIncluded)),
                                    !formBlocksValidationData[index] && (React.createElement(Tooltip, { title: t('Show errors') },
                                        React.createElement("div", { className: theme.block__header__invalid, onClick: () => {
                                                block.groups.forEach(gr => {
                                                    gr.forEach(it => {
                                                        form.blur(it.name);
                                                    });
                                                });
                                            } },
                                            React.createElement(CdnIcon, { icon: "Info/Warning" }))))),
                                block.subtitle && (React.createElement("div", { className: theme.block__header__subtitle }, t(block.subtitle.toString())))), (_a = block === null || block === void 0 ? void 0 : block.groups) === null || _a === void 0 ? void 0 :
                            _a.map((group, index) => {
                                return (React.createElement("div", { className: theme.groupCreate, key: index }, group === null || group === void 0 ? void 0 : group.map(field => {
                                    const type = getFieldType(field.type);
                                    const isEmail = field.type === FfpAccountFieldEnum.Email;
                                    const isDisabled = isEmail && !!initialValues['email'];
                                    const options = getFieldOptions(type, values, field.validationRules, field.type);
                                    return (React.createElement("div", { "data-field-name": field.name, className: cn(theme.groupCreate__field, {
                                            [theme.groupCreate__field_switch]: field.type === FfpAccountFieldEnum.Gender
                                        }), key: field.name },
                                        React.createElement(Field, { name: field.name, label: field.name, type: type, fieldType: field.type, value: field.value || '', validationRules: field.validationRules, options: options, translateValue: field.type === FfpAccountFieldEnum.DocType, disabled: isDisabled, onAutocompleteInputChange: onCityAutocompleteChange })));
                                })));
                            })));
                    })),
                    React.createElement(FormSpy, { subscription: {
                            errors: true,
                            hasValidationErrors: true,
                            values: true,
                            modified: true,
                            active: true
                        }, onChange: ({ errors, hasValidationErrors }) => {
                            setTimeout(() => {
                                var _a;
                                setValidationErrors(errors);
                                (_a = props.onErrorsChanges) === null || _a === void 0 ? void 0 : _a.call(props, hasValidationErrors);
                            }, 0);
                        } })),
                props.error && (React.createElement(ModalConfirm, { isOpen: !!props.error, size: "medium", title: t('Cannot create an account'), onClose: props.closeError, children: React.createElement(SafeHtml, { tag: "div", html: props.error }), confirmBtn: t('Close'), onConfirm: props.closeError }))));
        })));
};
export default CreateAccount;
